import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/ui-store';
import { useFocusTrap } from '@/hooks/useFocusTrap';

/**
 * Modal explaining data sovereignty and offline-mode guarantees.
 * Closes on backdrop click, Escape key, or ✕ button.
 */
export function PrivacyModal(): JSX.Element | null {
  const { t } = useTranslation();
  const open = useUiStore((s) => s.privacyModalOpen);
  const setOpen = useUiStore((s) => s.setPrivacyModalOpen);
  const trapRef = useFocusTrap(open);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      ref={trapRef}
      tabIndex={-1}
      className="fixed inset-0 z-[100] flex items-center justify-center outline-none"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      role="dialog"
      aria-modal
      aria-labelledby="privacy-modal-title"
    >
      <div
        className="relative rounded-xl border p-6 max-w-md w-full mx-4"
        style={{
          background: 'var(--color-card)',
          borderColor: 'var(--color-border)',
          boxShadow: '0 8px 32px var(--color-shadow)',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 rounded-md p-1 transition-colors"
          style={{ color: 'var(--color-muted)' }}
          aria-label={t('privacy.closeAria')}
        >
          <svg width="24" height="24" viewBox="0 0 18 18" fill="none" aria-hidden>
            <path d="M5 5l8 8M13 5l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-success)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <h2
            id="privacy-modal-title"
            className="text-lg font-semibold"
            style={{ color: 'var(--color-fg)' }}
          >
            {t('privacy.title')}
          </h2>
        </div>

        {/* Body */}
        <div className="space-y-3 text-sm" style={{ color: 'var(--color-muted)' }}>
          <PrivacySection
            heading={t('privacy.section.zeroServerHeading')}
            body={t('privacy.section.zeroServerBody')}
          />
          <PrivacySection
            heading={t('privacy.section.sovereigntyHeading')}
            body={t('privacy.section.sovereigntyBody')}
          />
          <PrivacySection
            heading={t('privacy.section.telemetryHeading')}
            body={t('privacy.section.telemetryBody')}
          />
          <PrivacySection
            heading={t('privacy.section.persistenceHeading')}
            body={t('privacy.section.persistenceBody')}
          />
        </div>

        {/* Footer */}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md px-4 py-2 text-sm font-medium transition-colors"
            style={{
              background: 'var(--color-accent)',
              color: '#fff',
            }}
          >
            {t('privacy.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrivacySection({
  heading,
  body,
}: {
  heading: string;
  body: string;
}): JSX.Element {
  return (
    <p>
      <strong style={{ color: 'var(--color-fg)' }}>{heading}</strong>{' '}
      {body}
    </p>
  );
}
